import { useState } from "react";
import {
  Boxes,
  Layers,
  ChevronDown,
  Info,
  CheckCircle,
  Clock,
  AlertCircle,
  FileCheck,
  Tag,
  DollarSign,
  Calendar,
  Building,
  ExternalLink,
} from "lucide-react";
import {
  Badge,
  Button,
  DetailField,
  DetailFieldGrid,
  DetailSectionCard,
  StatusBadge,
} from "@/shared/ui";
import { DatePicker } from "@/shared/ui/core/DatePicker";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/shared/ui/core/Collapsible";
import { cn } from "@/shared/lib/cn";

export function TestUiBlocksSection() {
  const [collapsibleOpen, setCollapsibleOpen] = useState(true);
  const [demoDate, setDemoDate] = useState("2026-09-28");
  const [demoDateTime, setDemoDateTime] = useState("2026-09-30T16:30");

  return (
    <section className="space-y-6">
      {/* Intro */}
      <div>
        <h2 className="m-0 text-h2 font-bold text-text">
          Chuẩn Hóa Các Khối Thông Tin & Section (Blocks & Grids Standard)
        </h2>
        <p className="m-0 text-caption text-text-muted">
          Giải quyết triệt để tình trạng lệch padding, khoảng cách, font chữ và đường kẻ giữa các module
        </p>
      </div>

      {/* Pattern 1: DetailFieldGrid với Row Dividers */}
      <DetailSectionCard
        title="1. Chuẩn Lưới Trường Thông Tin (DetailFieldGrid 2 Cột)"
        icon={<Boxes className="size-4" />}
        headerDivider
        headerEnd={<Badge tone="success" label="Đã chuẩn hóa UI" />}
        description="Đường kẻ phân cách hàng đồng bộ, nhãn tiêu đề trường màu đậm rõ nét, font Mono chuẩn cho mã/số"
      >
        <DetailFieldGrid columns={2}>
          <DetailField label="Mã định danh hệ thống">
            <div className="flex items-center gap-2">
              <span className="rounded bg-bg border border-border px-2 py-0.5 font-mono text-caption font-bold text-primary">
                MO-2026-0318
              </span>
              <span className="text-caption text-text-muted">(Phát sinh tự động)</span>
            </div>
          </DetailField>

          <DetailField label="Trạng thái phê duyệt">
            <StatusBadge status="In-progress" />
          </DetailField>

          <DetailField label="Đơn vị gia công / Phân xưởng">
            Nhà máy Chế biến Thủy hải sản Sóc Trăng (STVQ)
          </DetailField>

          <DetailField label="Ca sản xuất & Giờ bắt đầu">
            <span className="font-mono">Ca sáng (06:00 - 14:00) · 28/09/2026</span>
          </DetailField>

          <DetailField label="Kỹ thuật viên KCS trưởng ca">
            <span className="font-semibold">Trần Thị Mai (KCS Bậc 4)</span>
          </DetailField>

          <DetailField label="Chuyên viên Đảm bảo chất lượng (QA)">
            <div className="flex items-center gap-2">
              <span className="rounded bg-warning-soft px-2 py-0.5 text-caption font-medium text-warning-fg border border-warning/40">
                Chưa phân công ca này
              </span>
              <button
                type="button"
                className="text-caption font-semibold text-primary hover:underline cursor-pointer"
              >
                Phân công ngay →
              </button>
            </div>
          </DetailField>

          <DetailField label="Chỉ dẫn đặc biệt từ Trưởng ban ATTP" fullWidth>
            <div className="rounded-control bg-bg/70 p-2.5 text-body text-text border border-border/60">
              Lô hàng này phục vụ thị trường Châu Âu (EU). Bắt buộc duy trì nhiệt độ tâm fillet dưới -18°C ngay sau công đoạn mạ băng. Kiểm tra độ nhạy máy dò kim loại 30 phút/lần.
            </div>
          </DetailField>
        </DetailFieldGrid>
      </DetailSectionCard>

      {/* Pattern 2: Collapsible DetailSectionCard */}
      <DetailSectionCard
        title="2. Khối Thu Gọn / Mở Rộng Thông Minh (Collapsible Section Card)"
        description="Dành cho các thông tin hỗ trợ cấp 3 (L3 Supporting) hoặc nhật ký dài"
        collapsible={true}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-control border border-border bg-bg/40 p-3">
            <span className="text-label text-text font-semibold">Hạn ngạch xuất khẩu</span>
            <div className="mt-1 font-mono font-bold text-text text-body">EU-QUOTA-2026</div>
            <span className="text-success-fg text-caption">Còn hiệu lực</span>
          </div>
          <div className="rounded-control border border-border bg-bg/40 p-3">
            <span className="text-label text-text font-semibold">Chứng chỉ BRCGS Food v9</span>
            <div className="mt-1 font-mono font-bold text-text text-body">BRC-VN-88412</div>
            <span className="text-success-fg text-caption">Grade A</span>
          </div>
          <div className="rounded-control border border-border bg-bg/40 p-3">
            <span className="text-label text-text font-semibold">Mã vùng nuôi thủy sản (VietGAP)</span>
            <div className="mt-1 font-mono font-bold text-text text-body">VN-ST-0419</div>
            <span className="text-text-secondary text-caption">Xã An Thạnh 1, Cù Lao Dung</span>
          </div>
        </div>
      </DetailSectionCard>

      {/* Pattern 3: Nested Related Table in Card */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <div className="flex items-center justify-between border-b border-border/60 pb-2">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Layers className="size-4 text-primary" />
              <span>3. Khối Bảng Phụ Lồng Ghép (Nested Related Table)</span>
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Đảm bảo cuộn ngang nội bộ, không làm phình to hoặc rớt dòng layout chính
            </p>
          </div>
          <Button variant="secondary" size="sm" className="h-8 text-caption">
            <ExternalLink className="mr-1 size-3.5" />
            Xem toàn bộ bảng kê
          </Button>
        </div>

        <div className="overflow-x-auto rounded-control border border-border/70 bg-surface">
          <table className="w-full text-left text-caption border-collapse">
            <thead>
              <tr className="border-b border-border bg-bg/80 text-text-secondary uppercase">
                <th className="py-2.5 px-3">Thời gian ghi nhận</th>
                <th className="py-2.5 px-3">Điểm kiểm soát (CCP)</th>
                <th className="py-2.5 px-3">Chỉ số thực đo</th>
                <th className="py-2.5 px-3">Ngưỡng giới hạn cho phép</th>
                <th className="py-2.5 px-3 text-center">Kết luận</th>
                <th className="py-2.5 px-3">Người đo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60 text-body">
              <tr className="hover:bg-bg/40">
                <td className="py-2 px-3 font-mono text-caption text-text-muted">
                  09:00 28/09
                </td>
                <td className="py-2 px-3 font-medium text-text">
                  CCP-1: Nhiệt độ nước rửa khử trùng Chlorine
                </td>
                <td className="py-2 px-3 font-mono font-bold text-text">12.5°C (35 ppm)</td>
                <td className="py-2 px-3 font-mono text-caption text-text-secondary">≤ 15.0°C (30-50 ppm)</td>
                <td className="py-2 px-3 text-center">
                  <Badge tone="success" label="Đạt chuẩn" />
                </td>
                <td className="py-2 px-3 text-caption text-text-secondary">Nguyễn Văn Bình</td>
              </tr>
              <tr className="hover:bg-bg/40">
                <td className="py-2 px-3 font-mono text-caption text-text-muted">
                  10:30 28/09
                </td>
                <td className="py-2 px-3 font-medium text-text">
                  CCP-2: Kiểm tra mảnh kim loại qua máy dò
                </td>
                <td className="py-2 px-3 font-mono font-bold text-text">Không phát hiện</td>
                <td className="py-2 px-3 font-mono text-caption text-text-secondary">0 mảnh dị vật</td>
                <td className="py-2 px-3 text-center">
                  <Badge tone="success" label="Đạt chuẩn" />
                </td>
                <td className="py-2 px-3 text-caption text-text-secondary">Trần Thị Mai</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Pattern 4: Chuẩn Chọn Ngày & Giờ (DatePicker & DateTimePicker) */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between border-b border-border/60 pb-2">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Calendar className="size-4 text-primary" />
              <span>4. Chuẩn Bộ Chọn Ngày & Giờ (DatePicker & DateTimePicker)</span>
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Thay thế hoàn toàn lịch trình duyệt mặc định: Lịch tiếng Việt chuẩn ERP, hỗ trợ chọn giờ phút, phím tắt Hôm nay / Xóa
            </p>
          </div>
          <Badge tone="primary" label="Chuẩn Form TM Food" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
          {/* Demo 1: Date Only */}
          <div className="rounded-control border border-border/70 bg-bg/30 p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-text">1. Chọn Ngày (Date Only)</span>
              <Badge tone="success" label="mode='date'" />
            </div>
            <p className="text-[11px] text-text-muted m-0">Dùng cho ngày chứng từ, ngày sản xuất, hạn sử dụng</p>
            <div className="pt-1">
              <DatePicker
                mode="date"
                value={demoDate}
                onChange={(val) => setDemoDate(val)}
                placeholder="Chọn ngày sản xuất..."
                className="h-[36px] min-h-[36px]"
              />
            </div>
            <div className="text-[10.5px] font-mono text-text-muted pt-0.5">
              Giá trị ISO: <span className="text-primary font-bold">{demoDate || "(Trống)"}</span>
            </div>
          </div>

          {/* Demo 2: DateTime with Hour & Minute */}
          <div className="rounded-control border border-border/70 bg-bg/30 p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-text">2. Chọn Ngày & Giờ (DateTime)</span>
              <Badge tone="info" label="mode='datetime'" />
            </div>
            <p className="text-[11px] text-text-muted m-0">Dùng cho giờ nhập kho cấp đông, hạn SLA, ca sản xuất</p>
            <div className="pt-1">
              <DatePicker
                mode="datetime"
                value={demoDateTime}
                onChange={(val) => setDemoDateTime(val)}
                placeholder="Chọn ngày & giờ SLA..."
                className="h-[36px] min-h-[36px]"
              />
            </div>
            <div className="text-[10.5px] font-mono text-text-muted pt-0.5">
              Giá trị ISO: <span className="text-primary font-bold">{demoDateTime || "(Trống)"}</span>
            </div>
          </div>

          {/* Demo 3: Disabled state */}
          <div className="rounded-control border border-border/70 bg-bg/30 p-3 space-y-1.5 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-text">3. Trạng Thái Vô Hiệu Hóa</span>
              <Badge tone="neutral" label="disabled" />
            </div>
            <p className="text-[11px] text-text-muted m-0">Khi chứng từ đã khóa hoặc người dùng chỉ có quyền xem</p>
            <div className="pt-1">
              <DatePicker
                mode="date"
                value="2026-09-28"
                disabled
                placeholder="Không thể chỉnh sửa..."
                className="h-[36px] min-h-[36px]"
              />
            </div>
            <div className="text-[10.5px] font-mono text-text-muted pt-0.5">
              Chế độ chỉ đọc (Read-only / Locked)
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
